<script setup lang="ts">
import { useScopedQuery } from "@/api/query";
import { AuditFilter } from "@/components/audit/audit-filter.type";
import { useAuditNameResolver } from "@/components/audit/audit-name-resolver";
import AuditDiffViewer from "@/components/audit/AuditDiffViewer";
import AuditFiltersConfiguration from "@/components/audit/AuditFiltersConfiguration";
import AuditSpaceBadge from "@/components/audit/AuditSpaceBadge.vue";
import AuditTargetDisplay from "@/components/audit/AuditTargetDisplay";
import Frame from "@/components/common/Frame.vue";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import UserBadge from "@/components/users/UserBadge";
import { mdiEye } from "@mdi/js";
import { Audit, AuditQuery, AuditTarget, useApi } from "@soft-db/monitoring-api-client";
import {
	Badge,
	Button,
	Card,
	DataTableContent,
	DataTablePagination,
	DataTableRoot,
	Divider,
	Icon,
	makeColumnHelper,
} from "@soft-db/ui";
import { useRouteQuery } from "@vueuse/router";
import { DateTime } from "luxon";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

const api = useApi().audit();

const filters = ref<AuditFilter[]>([]);
const pageIndex = useRouteQuery<string, number>("pageIndex", "0", {
	transform: (v) => Number(v),
});
const pageSize = useRouteQuery<string, number>("pageSize", "10", {
	transform: (v) => Number(v),
});

/**
 * The time ref is only saved in the url when the page index is not 0.
 * This allow the page to be refreshed or send to someone else and still have the same elements in the audit table.
 */
const now = ref(typeof route.query.ref === "string" ? new Date(route.query.ref) : new Date());
watch(pageIndex, () => {
	if (pageIndex.value !== 0) {
		router.replace({ query: { ...route.query, ref: now.value.toISOString() } });
	} else {
		const query = { ...route.query };
		delete query.ref;
		router.replace({ query });
	}
});

const auditQuery = computed<AuditQuery>(() => {
	const query: AuditQuery = {};

	for (const filter of filters.value) {
		if (filter.type === "date") {
			query.to = filter.to;
			query.from = filter.from;
		}

		if (filter.type === "target") {
			query.target = filter.target;
			query.id = filter.targetId;
			query.parent = filter.parentId;
		}

		if (filter.type === "user") {
			query.user = filter.userId;
		}
	}

	return query;
});

async function saveFilterInURL() {
	pageIndex.value = 0;
	await nextTick();

	const filterKeys: (keyof AuditQuery)[] = ["to", "from", "target", "id", "parent", "user"];
	const previousQuery = { ...route.query };

	for (const key of filterKeys) {
		if (key in previousQuery && !(key in auditQuery.value)) {
			delete previousQuery[key];
		}
	}

	router.replace({
		query: {
			...previousQuery,
			...JSON.parse(JSON.stringify(auditQuery.value)),
		},
	});
}
watch(auditQuery, () => {
	saveFilterInURL();
});

function isValidQueryParam<T>(maybeArr: T | T[]): maybeArr is NonNullable<T> {
	return maybeArr != null && !Array.isArray(maybeArr);
}

function loadFiltersFromURL() {
	const { query } = route;

	if (isValidQueryParam(query.from) && isValidQueryParam(query.to)) {
		filters.value.push({
			type: "date",
			from: new Date(query.from),
			to: new Date(query.to),
		});
	}

	if (isValidQueryParam(query.target) && isValidQueryParam(query.id)) {
		filters.value.push({
			type: "target",
			target: query.target as AuditTarget,
			targetId: query.id.toString(),
			parentId: isValidQueryParam(query.parent) ? query.parent.toString() : undefined,
		});
	}

	if (isValidQueryParam(query.user)) {
		filters.value.push({
			type: "user",
			userId: query.user.toString(),
		});
	}
}

onMounted(() => {
	loadFiltersFromURL();
});

const { data } = useScopedQuery({
	key: () => [
		"audit",
		JSON.stringify(auditQuery.value),
		pageIndex.value,
		pageSize.value,
		now.value.toISOString(),
	],
	query: () =>
		api.fetchAudit({
			...auditQuery.value,
			to: auditQuery.value.to || now.value,
			pageIndex: pageIndex.value,
			pageSize: pageSize.value,
		}),
	placeholderData: (p) => p,
});

const currentViewedAudit = ref<Audit>();

const { resolveActionName } = useAuditNameResolver();

const { Column } = makeColumnHelper<Audit>();
</script>

<template>
	<Frame>
		<Card variant="outlined" content>
			<h3>{{ t("audit.filtersTitle") }}</h3>
			<AuditFiltersConfiguration v-model:filters="filters" />

			<Divider />
			<DataTableRoot
				:pagination="{ pageIndex, pageSize }"
				:data="data?.events || []"
				:getRowId="(_, idx) => String(idx)"
				manualPagination
				@update:pagination="
					(p) => {
						pageIndex = p.pageIndex;
						pageSize = p.pageSize;
					}
				"
			>
				<DataTableContent>
					<Column v-slot="{ getValue }" field="time" :header="t('common.date')">
						{{ DateTime.fromJSDate(getValue()).toLocaleString(DateTime.DATETIME_MED) }}
					</Column>

					<Column v-slot="{ row }" field="target" :header="t('common.object')">
						<AuditTargetDisplay :audit="row.original" />
					</Column>

					<PermissionGuard permission="space.get">
						<Column v-slot="{ getValue }" field="scope" :header="t('object.space')">
							<AuditSpaceBadge v-if="getValue().type === 'space'" :spaceId="getValue().id" />
						</Column>
					</PermissionGuard>

					<Column v-slot="{ getValue }" field="action" :header="t('common.action')">
						{{ resolveActionName(getValue()) }}
					</Column>

					<Column v-slot="{ getValue }" field="user" :header="t('object.user')">
						<UserBadge v-if="getValue()" :id="getValue()" />
						<Badge v-else color="info">
							{{ t("audit.serverChange") }}
						</Badge>
					</Column>

					<Column id="actions" v-slot="{ row }">
						<div class="float-right">
							<Button variant="soft" @click="currentViewedAudit = row.original">
								<Icon :d="mdiEye" />
							</Button>
						</div>
					</Column>
				</DataTableContent>
				<div class="flex justify-end">
					<DataTablePagination infinite :hasMore="data?.hasMore" />
				</div>
			</DataTableRoot>
		</Card>

		<AuditDiffViewer v-model:log="currentViewedAudit" />
	</Frame>
</template>
